import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { initMetaPixel, trackMetaEvent } from "@/lib/meta-pixel";
import { BrandLogo } from "@/components/BrandLogo";
import {
  isReviewMode,
  resolveOutcome,
  resolveStormVersion,
  STORM_BYLINE,
  STORM_DISCLAIMER,
  STORM_OFFER,
} from "./storm-landing";
import { StormOfferCard } from "./StormOfferCard";
import { StormReviewBar } from "./StormReviewBar";
import { Testimonials } from "@/components/Testimonials";

/**
 * Ten near-identical pages indexed by Google would compete with each other and
 * with the real site, so the page asks not to be indexed. Added on mount and
 * removed on unmount, because this is a SPA and the next page is not an ad page.
 * public/_headers sends the same instruction as a header for crawlers that do
 * not run scripts.
 */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

/**
 * PageView once per visit; nothing at all when no pixel id is configured, or
 * on the review link, so Roger reading drafts never counts as ad traffic.
 */
function useMetaPageView(enabled: boolean) {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !enabled) return;
    sent.current = true;
    if (initMetaPixel()) trackMetaEvent("PageView");
  }, [enabled]);
}

export function StormLandingPage() {
  const { search } = useLocation();
  const version = useMemo(() => resolveStormVersion(search), [search]);
  const review = isReviewMode(search);

  useDocumentMeta({
    title: `${version.hook} — Dividends Roundtable`,
    description: STORM_OFFER.subtitle,
  });
  useNoIndex();
  useMetaPageView(!review);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {review && <StormReviewBar version={version} />}
      <header className="border-b border-border px-4 py-1.5 sm:py-3">
        <div className="mx-auto flex max-w-xl items-center gap-2">
          <BrandLogo className="h-9 w-9" />
          <p className="font-heading text-base font-bold sm:text-lg">Dividends Roundtable</p>
        </div>
      </header>

      <main className="flex-1 px-4 pt-4 pb-6 sm:py-12">
        <div className="mx-auto max-w-xl">
          {/* Roger's face before the question: on a phone a byline under the
              form is never seen, and the person writing is what earns trust. */}
          <div className="mb-3 flex items-center gap-3">
            <img
              src="/images/roger_profile.jpg"
              alt="Roger Conrad"
              className="h-20 w-20 rounded-full border-2 border-primary/40 object-cover"
            />
            <p className="text-sm leading-snug text-muted-foreground sm:text-base">
              <span className="text-base font-semibold text-foreground sm:text-lg">Roger Conrad</span>
              <br />
              {STORM_BYLINE}
            </p>
          </div>

          <h1 className="font-heading text-2xl font-bold leading-tight sm:text-4xl">
            {version.hook}
          </h1>
          {/* One storm sentence above the form, the rest below it: on a phone the
              button has to show without scrolling. */}
          <StormSentence text={version.storm[0]} />

          {/* Most ad traffic is on phones: the form has to sit near the top. */}
          <StormOfferCard
            landingVariant={version.slug}
            outcome={resolveOutcome(version, review)}
            review={review}
          />
          {/* Points into the guide, so only once the guide exists. Below the
              form for the same reason as the second storm sentence. */}
          {STORM_OFFER.download && version.principles && (
            <p className="mt-4 text-sm font-semibold text-primary">{version.principles}</p>
          )}
          {version.storm.slice(1).map((sentence) => (
            <StormSentence key={sentence} text={sentence} />
          ))}

          {/* After the pitch, not above the form: the button still has to show
              without scrolling on a phone. The quotes are the readers' words,
              so the copy rules' test lets them name tickers and returns. */}
          <section className="mt-8" aria-labelledby="storm-readers">
            <h2 id="storm-readers" className="font-heading text-xl font-bold sm:text-2xl">
              Trusted by 1,500+ Substack subscribers
            </h2>
            {/* Counts given by the owner on 2026-10-27: Roger's Substack list,
                and Capitalist Times' paying subscribers. Recheck before
                raising either. */}
            <p className="mt-1 text-sm text-foreground sm:text-base">
              &hellip;and 1,200 paying subscribers at Capitalist Times, the company Elliot Gue and I
              founded.
            </p>
            <Testimonials />
          </section>

          <p className="mt-8 text-xs text-muted-foreground" data-testid="storm-disclaimer">
            {STORM_DISCLAIMER}
          </p>
        </div>
      </main>

      <footer className="border-t border-border px-4 py-4">
        <nav className="mx-auto flex max-w-xl gap-4 text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/disclaimer" className="hover:text-foreground">Disclaimer</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
        </nav>
      </footer>
    </div>
  );
}

function StormSentence({ text }: { text: string }) {
  return (
    <p className="mt-3 text-base leading-snug text-muted-foreground sm:mt-4 sm:text-lg sm:leading-normal">
      {text}
    </p>
  );
}
